// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/layout/Layout";
import PermissionPopup from "../components/auth/NoAccessPage";
import {
  CheckCircle,
  Shield,
  Users,
  Building,
  Calendar,
  CreditCard,
  Brain,
} from "lucide-react";

const LandingPage = () => {
  const [searchParams] = useSearchParams();
  const [showPermissionPopup, setShowPermissionPopup] = useState(false);

  useEffect(() => {
    // Check if we should show the permission popup
    const shouldShowPopup = searchParams.get("showPermissionPopup") === "true";
    if (shouldShowPopup) {
      setShowPermissionPopup(true);
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams]);

  return (
    <Layout>
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
        {/* Permission Popup */}
        {showPermissionPopup && (
          <PermissionPopup
            isOpen={showPermissionPopup}
            onClose={() => setShowPermissionPopup(false)}
            showLogout={true}
          />
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              VHP Booking <span className="text-blue-600">System</span>
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-8">
              Digitale Plattform für urbane Räume
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-12 leading-relaxed">
              Stadtplatz.de ist die innovative Buchungsplattform für öffentliche
              Räume, Plätze und Veranstaltungsflächen in Städten und Gemeinden.
              Wir digitalisieren die Verwaltung urbaner Räume und machen sie für
              Bürger, Vereine und Veranstalter einfach zugänglich.
            </p>
          </div>

          {/* Features Section */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Unsere Features
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-3">
                  <Calendar className="h-6 w-6 text-blue-600 mr-3" />
                  <span className="font-semibold text-gray-900">
                    Einfache Online-Buchung
                  </span>
                </div>
                <p className="text-gray-600">
                  Öffentliche Räume einfach und schnell online buchen
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                  <span className="font-semibold text-gray-900">
                    Transparente Verfügbarkeit
                  </span>
                </div>
                <p className="text-gray-600">
                  Echtzeit-Anzeige der Verfügbarkeit aller Räume
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-3">
                  <Building className="h-6 w-6 text-purple-600 mr-3" />
                  <span className="font-semibold text-gray-900">
                    Automatisierte Genehmigung
                  </span>
                </div>
                <p className="text-gray-600">
                  Schnelle und effiziente Genehmigungsverfahren
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-3">
                  <Users className="h-6 w-6 text-indigo-600 mr-3" />
                  <span className="font-semibold text-gray-900">
                    Digitale Verwaltung
                  </span>
                </div>
                <p className="text-gray-600">
                  Vollständige digitale Verwaltung von Veranstaltungen
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-3">
                  <Users className="h-6 w-6 text-orange-600 mr-3" />
                  <span className="font-semibold text-gray-900">
                    Bürgerbeteiligung
                  </span>
                </div>
                <p className="text-gray-600">
                  Community-Building und Bürgerbeteiligung fördern
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-3">
                  <Brain className="h-6 w-6 text-teal-600 mr-3" />
                  <span className="font-semibold text-gray-900">
                    KI-gestützte Bewertung
                  </span>
                </div>
                <p className="text-gray-600">
                  Intelligente Risikobewertung durch KI
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow md:col-span-2 lg:col-span-3">
                <div className="flex items-center mb-3">
                  <CreditCard className="h-6 w-6 text-emerald-600 mr-3" />
                  <span className="font-semibold text-gray-900">
                    Integrierte Zahlungsabwicklung
                  </span>
                </div>
                <p className="text-gray-600">
                  Sichere und einfache Zahlungsabwicklung direkt in der
                  Plattform
                </p>
              </div>
            </div>
          </div>

          {/* Target Audience Section */}
          <div className="mb-16">
            <div className="bg-white rounded-lg p-8 shadow-md">
              <p className="text-lg text-gray-700 text-center leading-relaxed">
                Geeignet für Städte und Kommunen, Stadtentwicklungsämter,
                Projektträger im öffentlichen Raum sowie Vereine, Initiativen
                und Veranstalter.
              </p>
            </div>
          </div>

          {/* Access Control Section */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-8">
            <div className="flex items-center justify-center mb-6">
              <Shield className="h-8 w-8 text-red-600 mr-3" />
              <h3 className="text-2xl font-bold text-red-800">
                🔐 Zugang nur für autorisierte Nutzer
              </h3>
            </div>
            <div className="text-center">
              <p className="text-lg text-red-700 max-w-3xl mx-auto leading-relaxed">
                Dieses Backend ist ausschließlich für Mitarbeiter der{" "}
                <strong>USG (Urban Space Governance)</strong> und autorisierte
                Administratoren zugänglich. Für den Zugang benötigen Sie einen
                gültigen Account mit entsprechenden Berechtigungen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LandingPage;
